import { useEffect, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Badge, Button } from "@vimos/ui";
import type { DirectorySubmission, DirectorySubmissionStatus, StoreDirectoryRow } from "@vimos/api-client";
import { DIRECTORY_SOCIAL_LABELS, DIRECTORY_SOCIAL_NETWORKS, directoryCategories } from "@vimos/api-client";
import type { DIRECTORY_EDITABLE_FEATURES } from "@vimos/api-client";
import {
  fetchDirectoryStore,
  fetchSubmissionsPage,
  reviewSubmission,
  signedSubmissionFileUrl,
  type ReviewAction,
} from "./directoryApi";

const statusLabel: Record<DirectorySubmissionStatus, string> = {
  pending: "Pendiente",
  received: "Recibida",
  in_review: "En revision",
  changes_requested: "Cambios solicitados",
  approved: "Aprobada",
  rejected: "Rechazada",
};

const statusTone: Record<DirectorySubmissionStatus, "neutral" | "market" | "signal"> = {
  pending: "market",
  received: "market",
  in_review: "market",
  changes_requested: "neutral",
  approved: "signal",
  rejected: "neutral",
};

const editFeatureLabels: Record<(typeof DIRECTORY_EDITABLE_FEATURES)[number], string> = {
  "edit.name": "Nombre comercial",
  "edit.description": "Descripción",
  "edit.category": "Categoría",
  "edit.keywords": "Palabras clave",
  "edit.contact": "Contacto",
  "edit.location": "Ubicación",
  "edit.images": "Imágenes",
  "edit.profile_content": "Horarios y oferta",
};

interface ChangeRow {
  label: string;
  current: string;
  proposed: string;
}

function readable(value: unknown): string {
  if (value == null || value === "") return "Vacío";
  if (Array.isArray(value)) return value.length ? value.map(String).join(", ") : "Vacío";
  if (typeof value === "object") return JSON.stringify(value);
  return String(value);
}

function equalValue(left: unknown, right: unknown): boolean {
  if (Array.isArray(left) || Array.isArray(right)) {
    return JSON.stringify(left ?? []) === JSON.stringify(right ?? []);
  }
  return String(left ?? "").trim() === String(right ?? "").trim();
}

function imageLabel(value: string | null, count?: number): string {
  if (count !== undefined)
    return count === 0 ? "Sin imágenes" : `${count} ${count === 1 ? "imagen" : "imágenes"}`;
  return value ? "Imagen cargada" : "Sin imagen";
}

function categoryLabel(id: string | null): string {
  if (!id) return "Sin clasificación";
  for (const category of directoryCategories) {
    if (category.id === id) return category.label;
    const child = category.children.find((item) => item.id === id);
    if (child) return `${category.label} · ${child.label}`;
  }
  return id;
}

function buildEditChanges(submission: DirectorySubmission, store: StoreDirectoryRow): ChangeRow[] {
  const features = submission.edit_features ?? [];
  const rows: ChangeRow[] = [];
  const add = (
    label: string,
    current: unknown,
    proposed: unknown,
    displayCurrent?: string,
    displayProposed?: string,
  ) => {
    if (equalValue(current, proposed)) return;
    rows.push({
      label,
      current: displayCurrent ?? readable(current),
      proposed: displayProposed ?? readable(proposed),
    });
  };

  if (features.includes("edit.name")) add("Nombre comercial", store.name, submission.name);
  if (features.includes("edit.description")) add("Descripción", store.description, submission.description);
  if (features.includes("edit.category")) {
    add("Categoría", store.category, submission.category);
    add(
      "Clasificación del directorio",
      store.category_id,
      submission.category_id ?? null,
      categoryLabel(store.category_id),
      categoryLabel(submission.category_id ?? null),
    );
  }
  if (features.includes("edit.keywords")) add("Palabras clave", store.keywords, submission.keywords ?? []);
  if (features.includes("edit.profile_content")) {
    add("Horario de atención", store.business_hours, submission.business_hours);
    add("Productos y servicios", store.offerings, submission.offerings ?? []);
  }
  if (features.includes("edit.contact")) {
    add("Teléfono", store.phone, submission.phone);
    add("WhatsApp", store.whatsapp, submission.whatsapp);
    add("Sitio web", store.website_url ?? store.store_url, submission.website_url);
  }
  if (features.includes("edit.location")) {
    add("Departamento", store.department, submission.department);
    add("Provincia", store.province, submission.province);
    add("Distrito", store.district, submission.district);
    add("Dirección", store.address, submission.address);
    add("Google Maps", store.google_maps_url, submission.google_maps_url);
    const currentCoordinates = [store.latitude, store.longitude].filter((value) => value != null).join(", ");
    const proposedCoordinates = [submission.latitude, submission.longitude]
      .filter((value) => value != null)
      .join(", ");
    add("Coordenadas", currentCoordinates, proposedCoordinates);
  }

  for (const network of DIRECTORY_SOCIAL_NETWORKS) {
    if (!features.includes(`social.${network}`)) continue;
    add(DIRECTORY_SOCIAL_LABELS[network], store.social_links?.[network], submission.social_links?.[network]);
  }

  if (features.includes("edit.images")) {
    add(
      "Logo",
      store.logo_url,
      submission.logo_url,
      imageLabel(store.logo_url),
      imageLabel(submission.logo_url),
    );
    add(
      "Portada",
      store.cover_image_url,
      submission.cover_image_url,
      imageLabel(store.cover_image_url),
      imageLabel(submission.cover_image_url),
    );
    add(
      "Galería",
      store.gallery,
      submission.gallery,
      imageLabel(null, store.gallery?.length ?? 0),
      imageLabel(null, submission.gallery?.length ?? 0),
    );
  }
  return rows;
}

function EditChanges({ submission, store }: { submission: DirectorySubmission; store: StoreDirectoryRow }) {
  const changes = buildEditChanges(submission, store);
  return (
    <section className="mt-4 overflow-hidden rounded-lg border border-blue-100 bg-blue-50/40">
      <div className="flex flex-wrap items-center justify-between gap-2 border-b border-blue-100 px-4 py-3">
        <div>
          <h3 className="text-sm font-semibold text-slate-900">Cambios propuestos</h3>
          <p className="mt-0.5 text-xs text-slate-500">Comparación con la ficha vigente</p>
        </div>
        <span className="rounded-full bg-blue-100 px-2.5 py-1 text-[11px] font-semibold text-blue-800">
          {changes.length} {changes.length === 1 ? "cambio" : "cambios"}
        </span>
      </div>
      {changes.length ? (
        <div className="divide-y divide-blue-100">
          <div className="hidden grid-cols-[minmax(90px,0.7fr)_minmax(0,1fr)_minmax(0,1fr)] gap-3 px-4 py-2 text-[10px] font-bold uppercase tracking-wide text-slate-500 sm:grid">
            <span>Campo</span>
            <span>Actual</span>
            <span>Propuesto</span>
          </div>
          {changes.map((change) => (
            <div
              key={change.label}
              className="grid gap-2 px-4 py-3 sm:grid-cols-[minmax(90px,0.7fr)_minmax(0,1fr)_minmax(0,1fr)] sm:gap-3"
            >
              <p className="text-xs font-semibold text-slate-700">{change.label}</p>
              <div className="min-w-0">
                <p className="text-[10px] font-semibold uppercase tracking-wide text-slate-400 sm:hidden">
                  Actual
                </p>
                <p className="break-words text-xs leading-5 text-slate-500">{change.current}</p>
              </div>
              <div className="min-w-0 rounded-md bg-white/80 px-2.5 py-2 sm:bg-transparent sm:p-0">
                <p className="text-[10px] font-semibold uppercase tracking-wide text-blue-600 sm:hidden">
                  Propuesto
                </p>
                <p className="break-words text-xs font-semibold leading-5 text-slate-900">
                  {change.proposed}
                </p>
              </div>
            </div>
          ))}
        </div>
      ) : (
        <p className="px-4 py-4 text-sm text-slate-600">
          No detectamos diferencias frente a la ficha vigente en los campos autorizados.
        </p>
      )}
    </section>
  );
}

function SubmissionImages({ submission }: { submission: DirectorySubmission }) {
  const { data } = useQuery({
    queryKey: ["submission_files", submission.id],
    queryFn: async () => {
      const [logo, cover] = await Promise.all([
        signedSubmissionFileUrl(submission.logo_url),
        signedSubmissionFileUrl(submission.cover_image_url),
      ]);
      const gallery = await Promise.all(
        (Array.isArray(submission.gallery) ? submission.gallery : []).map((path) =>
          signedSubmissionFileUrl(path),
        ),
      );
      return { logo, cover, gallery: gallery.filter((url): url is string => Boolean(url)) };
    },
  });

  if (!data) return null;
  if (!data.logo && !data.cover && data.gallery.length === 0) return null;

  return (
    <div className="mt-2 flex flex-wrap items-center gap-2">
      {data.logo ? <img src={data.logo} alt="Logo" className="h-14 w-14 rounded object-cover" /> : null}
      {data.cover ? <img src={data.cover} alt="Portada" className="h-14 w-24 rounded object-cover" /> : null}
      {data.gallery.map((url) => (
        <img key={url} src={url} alt="Galeria" className="h-14 w-14 rounded object-cover" />
      ))}
    </div>
  );
}

function SubmissionItem({ submission }: { submission: DirectorySubmission }) {
  const queryClient = useQueryClient();
  const [notes, setNotes] = useState("");
  const [reopenNotes, setReopenNotes] = useState("");
  const [isReopening, setIsReopening] = useState(false);
  const editTarget = useQuery({
    queryKey: ["stores_directory", "edit-submission-target", submission.store_id],
    queryFn: () => fetchDirectoryStore(submission.store_id!),
    enabled: Boolean(submission.edit_features && submission.store_id),
  });

  const review = useMutation({
    mutationFn: ({ action, notes: reviewNotes }: { action: ReviewAction; notes?: string }) =>
      reviewSubmission(submission.id, action, reviewNotes),
    onSuccess: () => {
      setIsReopening(false);
      setReopenNotes("");
      queryClient.invalidateQueries({ queryKey: ["directory_submissions"] });
      queryClient.invalidateQueries({ queryKey: ["stores_directory"] });
      queryClient.invalidateQueries({ queryKey: ["directory_billing_charges"] });
      queryClient.invalidateQueries({ queryKey: ["directory_billing_accounts"] });
    },
  });

  const isClosed = submission.status === "approved" || submission.status === "rejected";
  const editFields = (submission.edit_features ?? [])
    .map(
      (feature) =>
        editFeatureLabels[feature as keyof typeof editFeatureLabels] ??
        (feature.startsWith("social.") ? feature.slice(7) : null),
    )
    .filter((feature): feature is string => Boolean(feature));

  return (
    <li className="flex flex-col gap-3 px-4 py-4">
      <div className="flex flex-col gap-3 lg:flex-row lg:items-start lg:justify-between">
        <div>
          {submission.edit_features ? (
            <p className="text-gold mb-1 font-mono text-[11px] uppercase tracking-wide">
              Edición de ficha existente
            </p>
          ) : null}
          <p className="text-ink font-medium">{submission.name}</p>
          <p className="text-mist text-sm">
            {[submission.district, submission.province, submission.department].filter(Boolean).join(", ") ||
              "Sin ubicacion"}{" "}
            - {submission.category ?? "Sin categoria"}
          </p>
          <p className="text-mist text-xs">
            Recibida {new Date(submission.created_at).toLocaleDateString("es-PE")}
          </p>
          {submission.edit_features ? (
            <>
              <p className="text-mist mt-1 text-xs">
                Ficha asociada:{" "}
                {editTarget.data?.name ?? (editTarget.isLoading ? "Cargando…" : "No disponible")}
                {editTarget.data ? ` · /tienda/${editTarget.data.slug}/` : ""}
              </p>
              <p className="text-mist mt-1 text-xs">
                Campos autorizados: {editFields.join(", ") || "ninguno"}
              </p>
            </>
          ) : null}
          {submission.review_notes ? (
            <p className="text-ink mt-1 text-sm">Notas: {submission.review_notes}</p>
          ) : null}
          {submission.review_events?.length ? (
            <div className="mt-2 space-y-1 rounded-md bg-slate-50 px-3 py-2">
              <p className="text-[10px] font-semibold uppercase tracking-wide text-slate-500">
                Historial de revisión
              </p>
              {submission.review_events.map((event) => (
                <p key={event.id} className="text-xs text-slate-600">
                  {event.action === "rejected" ? "Rechazada" : "Reabierta"} ·{" "}
                  {new Date(event.created_at).toLocaleString("es-PE")}
                  {event.notes ? ` · ${event.notes}` : ""}
                </p>
              ))}
            </div>
          ) : null}
          <SubmissionImages submission={submission} />
        </div>
        <div className="flex flex-col items-start gap-2">
          <Badge tone={statusTone[submission.status]}>{statusLabel[submission.status]}</Badge>
        </div>
      </div>

      {submission.edit_features ? (
        editTarget.data ? (
          <EditChanges submission={submission} store={editTarget.data} />
        ) : editTarget.isLoading ? (
          <div className="mt-4 rounded-lg border border-slate-200 bg-slate-50 px-4 py-3 text-xs text-slate-500">
            Comparando la propuesta con la ficha vigente…
          </div>
        ) : (
          <div className="mt-4 rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-xs text-amber-800">
            No se pudo cargar la ficha actual; no es posible preparar la comparación.
          </div>
        )
      ) : null}

      {!isClosed ? (
        <div className="flex flex-col gap-2">
          <textarea
            className="border-sand/70 bg-paper text-ink focus-visible:ring-gold focus-visible:ring-offset-paper min-h-16 w-full rounded-md border px-3 py-2 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2"
            placeholder="Escribe aquí el mensaje para pedir cambios o el motivo del rechazo."
            value={notes}
            onChange={(event) => setNotes(event.target.value)}
          />
          {review.error ? (
            <p className="text-sm text-red-600">
              {review.error instanceof Error ? review.error.message : "No se pudo completar la accion."}
            </p>
          ) : null}
          <div className="flex flex-wrap gap-2">
            <Button
              size="sm"
              onClick={() => review.mutate({ action: "approve" })}
              isLoading={review.isPending}
            >
              Aprobar
            </Button>
            <Button
              size="sm"
              variant="outline"
              onClick={() => review.mutate({ action: "request_changes", notes })}
              disabled={notes.trim() === "" || review.isPending}
            >
              Pedir cambios
            </Button>
            <Button
              size="sm"
              variant="danger"
              onClick={() => {
                if (
                  window.confirm(
                    "¿Rechazar esta solicitud? Podrás reabrirla desde el historial, pero el motivo quedará registrado.",
                  )
                ) {
                  review.mutate({ action: "reject", notes });
                }
              }}
              disabled={!notes.trim() || review.isPending}
              isLoading={review.isPending}
            >
              Rechazar
            </Button>
          </div>
        </div>
      ) : null}

      {submission.status === "rejected" ? (
        <div className="flex flex-col gap-2">
          {!isReopening ? (
            <Button size="sm" variant="outline" onClick={() => setIsReopening(true)}>
              Reabrir solicitud
            </Button>
          ) : (
            <div className="rounded-lg border border-amber-200 bg-amber-50/60 p-3">
              <p className="text-sm font-semibold text-amber-950">Reabrir solicitud rechazada</p>
              <p className="mt-1 text-xs text-amber-900">
                Volverá a la cola de revisión. El rechazo original quedará en el historial.
              </p>
              <textarea
                className="mt-3 min-h-16 w-full rounded-md border border-amber-200 bg-white px-3 py-2 text-sm text-slate-900 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-amber-500"
                placeholder="Motivo de reapertura (obligatorio)"
                value={reopenNotes}
                onChange={(event) => setReopenNotes(event.target.value)}
                maxLength={1000}
              />
              {review.error ? (
                <p className="mt-2 text-sm text-red-600">
                  {review.error instanceof Error ? review.error.message : "No se pudo reabrir la solicitud."}
                </p>
              ) : null}
              <div className="mt-2 flex flex-wrap gap-2">
                <Button
                  size="sm"
                  onClick={() => review.mutate({ action: "reopen", notes: reopenNotes })}
                  disabled={!reopenNotes.trim() || review.isPending}
                  isLoading={review.isPending}
                >
                  Confirmar reapertura
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => {
                    setIsReopening(false);
                    setReopenNotes("");
                  }}
                >
                  Cancelar
                </Button>
              </div>
            </div>
          )}
        </div>
      ) : null}
    </li>
  );
}

export function SubmissionsPanel() {
  const [queue, setQueue] = useState<"open" | "closed">("open");
  const [page, setPage] = useState(1);
  const { data, isLoading, error } = useQuery({
    queryKey: ["directory_submissions", queue, page],
    queryFn: () => fetchSubmissionsPage(queue, page),
  });
  const submissions = data?.items ?? [];
  const totalPages = Math.max(1, Math.ceil((data?.total ?? 0) / 20));

  useEffect(() => {
    if (page > totalPages) setPage(totalPages);
  }, [page, totalPages]);

  if (isLoading) return <p className="text-mist mt-6">Cargando solicitudes...</p>;
  if (error) return <p className="mt-6 text-red-600">No pudimos cargar las solicitudes.</p>;

  return (
    <div className="mt-4">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h2 className="font-display text-ink text-lg font-bold">
            {queue === "open" ? "Cola de revisión" : "Historial resuelto"}
          </h2>
          <p className="text-mist mt-1 text-sm">{data?.total ?? 0} solicitudes · primero las más antiguas</p>
        </div>
        <div className="flex gap-2" role="group" aria-label="Estado de las solicitudes">
          {(
            [
              ["open", "Pendientes"],
              ["closed", "Historial"],
            ] as const
          ).map(([value, label]) => (
            <button
              key={value}
              type="button"
              aria-pressed={queue === value}
              onClick={() => {
                setQueue(value);
                setPage(1);
              }}
              className={`rounded-lg border px-3 py-2 text-xs font-semibold ${queue === value ? "border-blue-200 bg-blue-50 text-blue-800" : "border-slate-200 text-slate-600 hover:bg-slate-50"}`}
            >
              {label}
            </button>
          ))}
        </div>
      </div>
      <ul className="divide-sand/40 border-sand/40 bg-paper mt-4 divide-y rounded-lg border">
        {submissions.length === 0 ? (
          <li className="text-mist px-4 py-6 text-sm">
            {queue === "open"
              ? "No hay solicitudes pendientes de revisión."
              : "Todavía no hay solicitudes resueltas."}
          </li>
        ) : null}
        {submissions.map((submission) => (
          <SubmissionItem key={submission.id} submission={submission} />
        ))}
      </ul>
      {totalPages > 1 ? (
        <nav className="mt-4 flex items-center justify-between gap-3" aria-label="Paginación de solicitudes">
          <Button
            size="sm"
            variant="outline"
            disabled={page <= 1}
            onClick={() => setPage((current) => current - 1)}
          >
            Anterior
          </Button>
          <p className="text-mist text-sm">
            Página {page} de {totalPages} · 20 por página
          </p>
          <Button
            size="sm"
            variant="outline"
            disabled={page >= totalPages}
            onClick={() => setPage((current) => current + 1)}
          >
            Siguiente
          </Button>
        </nav>
      ) : null}
    </div>
  );
}
